import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { NODE_CATEGORIES } from './nodeCategories'
import { Lock } from 'lucide-react'
import { usePlanRights } from '@/hooks/usePlanRights'

interface Props {
  onDragStart: (event: React.DragEvent, nodeType: string) => void
}

function getCategoryDotColor(categoryId: string): string {
  switch (categoryId) {
    case 'control':
      return 'bg-slate-400'
    case 'conditions':
      return 'bg-amber-400'
    case 'interaction':
      return 'bg-blue-400'
    case 'routing':
      return 'bg-emerald-400'
    case 'ai':
      return 'bg-violet-400'
    default:
      return 'bg-gray-400'
  }
}

/**
 * Sidebar gauche avec les 11 node types organisés par catégorie.
 * Drag-and-drop pour ajouter un nœud au canvas.
 */
export function NodePalette({ onDragStart }: Props) {
  // Le nœud « Vers équipe » est inclus à partir de Team.
  const { allowed } = usePlanRights()
  const verrouille = (type: string) => (type === 'route_team' || type === 'queue') && !allowed('numeros_partages')
  const { t } = useTranslation()

  return (
    <div className="w-56 bg-[#131625] border-r border-white/[0.06] flex flex-col h-full">
      <div className="px-4 pt-5 pb-4">
        <h3 className="text-[13px] font-semibold text-white/90 tracking-wide">
          {t('manager.flows.editor.palette.steps')}
        </h3>
        <p className="text-[11px] text-white/35 mt-1 leading-relaxed">
          {t('manager.flows.editor.palette.dragToAdd')}
        </p>
      </div>

      <div className="flex-1 overflow-y-auto px-3 pb-4 space-y-5">
        {NODE_CATEGORIES.map((category) => {
          const dotColor = getCategoryDotColor(category.id)
          return (
            <div key={category.id}>
              <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-white/25 px-1 mb-2">
                {t(category.labelKey)}
              </p>
              <div className="space-y-1">
                {category.nodes.filter((node) => !node.horsPalette).map((node) => (
                  <div
                    key={node.type}
                    draggable={!verrouille(node.type)}
                    onDragStart={(e) => (verrouille(node.type) ? e.preventDefault() : onDragStart(e, node.type))}
                    title={verrouille(node.type) ? t('plan.includedIn', { plan: 'Team' }) : undefined}
                    className={cn(
                      'group flex items-center gap-2.5 px-3 py-2.5 rounded-lg',
                      verrouille(node.type) ? 'cursor-not-allowed opacity-50' : 'cursor-grab',
                      'bg-white/[0.03] border border-transparent',
                      'hover:bg-white/[0.07] hover:border-white/[0.08]',
                      'active:scale-[0.97] transition-all duration-150',
                    )}
                  >
                    <div
                      className={cn('w-2 h-2 rounded-full shrink-0', dotColor)}
                    />
                    <span className="text-[13px] text-white/70 font-medium group-hover:text-white/90 transition-colors truncate">
                      {t(node.labelKey)}
                    </span>
                    {verrouille(node.type) && <Lock className="w-3 h-3 ml-auto text-white/50 shrink-0" />}
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
